import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Container, Typography, Box, Paper, Chip, CircularProgress,
  Alert, IconButton, Tooltip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormGroup, FormControlLabel,
  Checkbox, Divider,
} from '@mui/material';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface PendingBand {
  _id: string;
  artistName: string;
  status: string;
  mainImage: string | null;
  genres: string[];
  createdAt: string;
  createdBy: { _id: string; name: string; email: string } | null;
  about: string;
}

const CONDITION_OPTIONS = [
  { id: 'music',  label: 'Missing or no music embed' },
  { id: 'images', label: 'Light on images — more photos needed' },
  { id: 'bio',    label: 'Bio / About section is too sparse' },
  { id: 'links',  label: 'Missing links (website, social media)' },
  { id: 'tech',   label: 'No technical rider or stage requirements' },
] as const;

const PendingBandsPage: React.FC = () => {
  const [pendingBands, setPendingBands] = useState<PendingBand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Approve dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [bandToApprove, setBandToApprove] = useState<PendingBand | null>(null);
  const [approving, setApproving] = useState(false);

  // Approve with Notes dialog
  const [approveWithNotesOpen, setApproveWithNotesOpen] = useState(false);
  const [bandForNotes, setBandForNotes] = useState<PendingBand | null>(null);
  const [conditionSelections, setConditionSelections] = useState<Set<string>>(new Set());
  const [conditionFreeText, setConditionFreeText] = useState('');
  const [approvingWithNotes, setApprovingWithNotes] = useState(false);

  // Rejection dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [bandToReject, setBandToReject] = useState<PendingBand | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => { fetchPendingBands(); }, []);

  const fetchPendingBands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/bands/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingBands(response.data);
    } catch {
      setError('Failed to fetch pending bands');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmApprove = async () => {
    if (!bandToApprove) return;
    try {
      setApproving(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/bands/${bandToApprove._id}/review`,
        { status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingBands(prev => prev.filter(b => b._id !== bandToApprove._id));
      setSuccess('Band approved — the artist has been notified by email.');
      setTimeout(() => setSuccess(''), 5000);
      setApproveDialogOpen(false);
      setBandToApprove(null);
    } catch {
      setError('Failed to approve band');
    } finally {
      setApproving(false);
    }
  };

  const handleConfirmApproveWithNotes = async () => {
    if (!bandForNotes) return;
    const selectedLabels = CONDITION_OPTIONS
      .filter(opt => conditionSelections.has(opt.id))
      .map(opt => opt.label);
    const conditionNotes = [
      ...selectedLabels,
      ...(conditionFreeText.trim() ? [conditionFreeText.trim()] : []),
    ].join('\n');

    try {
      setApprovingWithNotes(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/bands/${bandForNotes._id}/review`,
        { status: 'approved', conditionNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingBands(prev => prev.filter(b => b._id !== bandForNotes._id));
      setSuccess('Band approved with notes — the artist has been notified by email.');
      setTimeout(() => setSuccess(''), 5000);
      setApproveWithNotesOpen(false);
      setBandForNotes(null);
    } catch {
      setError('Failed to approve band with notes');
    } finally {
      setApprovingWithNotes(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!bandToReject) return;
    try {
      setRejecting(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/api/admin/bands/${bandToReject._id}/review`,
        { status: 'rejected', rejectionNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingBands(prev => prev.filter(b => b._id !== bandToReject._id));
      setSuccess('Band rejected — the artist has been notified by email.');
      setTimeout(() => setSuccess(''), 5000);
      setRejectDialogOpen(false);
      setBandToReject(null);
    } catch {
      setError('Failed to reject band');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IE', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const timeAgo = (dateString?: string) => {
    if (!dateString) return '';
    const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
    return `${Math.floor(days / 365)} year${Math.floor(days / 365) === 1 ? '' : 's'} ago`;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>Pending Bands</Typography>
        <Typography variant="body2" color="text.secondary">
          Bands submitted by artists awaiting your approval. Artists are notified by email on any decision.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : pendingBands.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'transparent' }}>
          <MusicNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">No bands pending review</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {pendingBands.map(band => (
            <Paper
              key={band._id}
              onClick={() => window.open(`/band/${band._id}`, '_blank')}
              sx={{
                overflow: 'hidden',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'stretch',
                ...(band.mainImage ? {
                  backgroundImage: `linear-gradient(to left, rgba(0,0,0,0.93) 0%, rgba(0,0,0,0.9) 18%, rgba(0,0,0,0.25) 30%, rgba(0,0,0,0) 40%), url(${band.mainImage})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                } : {}),
              }}
            >
              {/* Clickable info area */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4, p: 3 }}>

                {/* Band name — takes all available space on the left */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: 1.25, mb: 1, textShadow: '0 1px 4px rgba(0,0,0,0.9), 0 2px 12px rgba(0,0,0,0.8)' }}>
                    {band.artistName}
                  </Typography>
                  <Typography variant="caption" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.55)' : 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.25, textShadow: band.mainImage ? '0 1px 4px rgba(0,0,0,0.9)' : 'none' }}>
                    Submitted by
                  </Typography>
                  {band.createdBy ? (
                    <>
                      <Typography variant="body2" sx={{ color: band.mainImage ? 'white' : 'text.primary', textShadow: band.mainImage ? '0 1px 4px rgba(0,0,0,0.9)' : 'none' }}>
                        {band.createdBy.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.65)' : 'text.secondary', textShadow: band.mainImage ? '0 1px 4px rgba(0,0,0,0.9)' : 'none' }}>
                        {band.createdBy.email}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.65)' : 'text.secondary' }}>—</Typography>
                  )}
                </Box>

                {/* Genres — fixed width, vertical, sits in the gradient zone */}
                <Box sx={{ flex: '0 0 160px' }}>
                  <Typography variant="caption" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.55)' : 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.75 }}>
                    Genres
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {band.genres.slice(0, 3).map(g => (
                      <Chip
                        key={g}
                        label={g}
                        size="small"
                        sx={band.mainImage ? { bgcolor: 'rgba(0,0,0,0.55)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', alignSelf: 'flex-start' } : { alignSelf: 'flex-start' }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Submitted date — fixed width, furthest right before buttons */}
                <Box sx={{ flex: '0 0 160px' }}>
                  <Typography variant="caption" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.55)' : 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.8, display: 'block', mb: 0.25 }}>
                    Submitted
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: band.mainImage ? 'white' : 'text.primary', textShadow: band.mainImage ? '0 1px 4px rgba(0,0,0,0.9)' : 'none' }}>
                    {timeAgo(band.createdAt)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: band.mainImage ? 'rgba(255,255,255,0.55)' : 'text.secondary', textShadow: band.mainImage ? '0 1px 4px rgba(0,0,0,0.9)' : 'none' }}>
                    {formatDate(band.createdAt)}
                  </Typography>
                </Box>

              </Box>

              {/* Actions — stops card click from firing */}
              <Box
                onClick={e => e.stopPropagation()}
                sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pr: 3, flexShrink: 0 }}
              >
                <Tooltip title="Open band page">
                  <IconButton
                    size="small"
                    component="a"
                    href={`/band/${band._id}`}
                    target="_blank"
                    sx={{ color: band.mainImage ? 'white' : 'inherit' }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: 140 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => { setBandToApprove(band); setApproveDialogOpen(true); }}
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<AssignmentTurnedInIcon />}
                    onClick={() => {
                      setBandForNotes(band);
                      setConditionSelections(new Set());
                      setConditionFreeText('');
                      setApproveWithNotesOpen(true);
                    }}
                    fullWidth
                  >
                    With Notes
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => { setBandToReject(band); setRejectionNote(''); setRejectDialogOpen(true); }}
                    fullWidth
                  >
                    Reject
                  </Button>
                </Box>
              </Box>

            </Paper>
          ))}
        </Box>
      )}

      {/* Approve confirmation dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Band</DialogTitle>
        <DialogContent>
          {bandToApprove && (
            <Typography variant="body2">
              Approve <strong>{bandToApprove.artistName}</strong>
              {bandToApprove.createdBy && ` by ${bandToApprove.createdBy.name}`}?
              {' '}Their page will go live on imc.show and they'll be notified by email.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={approving}>Cancel</Button>
          <Button
            onClick={handleConfirmApprove}
            color="success"
            variant="contained"
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}
          >
            {approving ? 'Approving...' : 'Approve & Notify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve with Notes dialog */}
      <Dialog open={approveWithNotesOpen} onClose={() => setApproveWithNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve with Notes</DialogTitle>
        <DialogContent>
          {bandForNotes && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{bandForNotes.artistName}</strong> will go live, but you can flag areas to improve.
              They'll receive an approval email with your notes attached.
            </Typography>
          )}
          <Divider sx={{ mb: 2 }} />
          <FormGroup>
            {CONDITION_OPTIONS.map(opt => (
              <FormControlLabel
                key={opt.id}
                control={
                  <Checkbox
                    checked={conditionSelections.has(opt.id)}
                    onChange={e => {
                      setConditionSelections(prev => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(opt.id);
                        else next.delete(opt.id);
                        return next;
                      });
                    }}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional notes (optional)"
            placeholder="Any specific feedback for this artist..."
            value={conditionFreeText}
            onChange={e => setConditionFreeText(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveWithNotesOpen(false)} disabled={approvingWithNotes}>Cancel</Button>
          <Button
            onClick={handleConfirmApproveWithNotes}
            color="warning"
            variant="contained"
            disabled={approvingWithNotes || (conditionSelections.size === 0 && !conditionFreeText.trim())}
            startIcon={approvingWithNotes ? <CircularProgress size={16} /> : <AssignmentTurnedInIcon />}
          >
            {approvingWithNotes ? 'Sending...' : 'Approve & Send Notes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Band Submission</DialogTitle>
        <DialogContent>
          {bandToReject && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Rejecting <strong>{bandToReject.artistName}</strong>
              {bandToReject.createdBy && ` by ${bandToReject.createdBy.name}`}.
              An email will be sent to the artist automatically.
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for rejection (optional)"
            placeholder="Let the artist know why their submission was not accepted and what they can do to improve it..."
            value={rejectionNote}
            onChange={e => setRejectionNote(e.target.value)}
            autoFocus
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            If left blank, the artist will be told the submission was not accepted but no specific reason will be given.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={rejecting}>Cancel</Button>
          <Button
            onClick={handleConfirmReject}
            color="error"
            variant="contained"
            disabled={rejecting}
            startIcon={rejecting ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {rejecting ? 'Rejecting...' : 'Reject & Notify Artist'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PendingBandsPage;
